import { ColumnInstance } from 'react-table';

import { IColumnProps } from './tableTypes';

type AnyColumn = ColumnInstance<IColumnProps>;

/**
 * Walk up the react-table parent links to the top-level (depth 0) column of the
 * group `column` belongs to. Returns the column itself when it has no parent.
 */
export function topLevelColumn(column: AnyColumn): AnyColumn | undefined {
  let current = column;
  while (current?.parent) current = current.parent as AnyColumn;
  return current;
}

function descendantIds(column: AnyColumn): string[] {
  const children = column?.columns as AnyColumn[] | undefined;
  if (!children?.length) return [];
  return children.flatMap((child) => [child.id, ...descendantIds(child)]);
}

/**
 * Every column id that must freeze together for the first column to read as sticky. The root id is
 * included too, or the depth-0 (often empty) group header leaves the top-left corner unstuck.
 */
export function getStickyColumnIds(column: AnyColumn): string[] {
  const root = topLevelColumn(column);
  if (!root) return [];
  return [root.id, ...descendantIds(root)];
}

/**
 * A pin freezes a whole top-level column, so any pinned id in its tree freezes every header row of it.
 * Per-id matching left the title row scrolling: the Table Manager never stores the depth-0 id.
 */
export function isInPinnedTree(header: AnyColumn, stickyColumns: string[]): boolean {
  // Header rows hold placeholder chains and parent copies (`id: "<id>_<uid>"` plus `originalId`).
  let column = header;
  while (column?.placeholderOf) column = column.placeholderOf as AnyColumn;
  const root = topLevelColumn(column) as (AnyColumn & { originalId?: string }) | undefined;
  if (!root) return false;
  return [root.originalId ?? root.id, ...descendantIds(root)].some((id) => stickyColumns.includes(id));
}
